// ---------------------------------------------------------------------------
// Clipboard Slice — copy, cut, paste, duplicate for selected elements.
//
// The clipboard is stored in the Zustand store (not the browser clipboard API)
// to keep things simple and avoid async permission prompts.
// ---------------------------------------------------------------------------

import type { CogoElement, CogoBlockDef } from './types'
import { findElementInTree, findParentInTree, collectElementIds } from './canvas-slice'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ClipboardOperation = 'copy' | 'cut' | null

export interface ClipboardState {
  elements: CogoElement[]
  sourceBlockName: string | null
  operation: ClipboardOperation
}

export interface ClipboardSlice {
  clipboard: ClipboardState

  copySelection: () => void
  cutSelection: () => void
  pasteClipboard: () => void
  duplicateSelection: () => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generate a short unique ID for pasted elements. */
function generateElementId(): string {
  return crypto.randomUUID().slice(0, 8)
}

/** Deep clone a CogoElement tree, assigning fresh IDs to every node. */
function deepCloneWithNewIds(element: CogoElement): CogoElement {
  const clone = structuredClone(element)
  clone.id = generateElementId()
  if (clone.children) {
    clone.children = clone.children.map(deepCloneWithNewIds)
  }
  return clone
}

// ---------------------------------------------------------------------------
// Slice state type (what we need from the combined store)
// ---------------------------------------------------------------------------

interface StoreView {
  // Selection
  selectedIds: string[]
  selectedBlockName: string | null
  // Canvas
  blocks: Record<string, CogoBlockDef>
  app: import('./types').CogoAppDef | null
  activePageId: string | null
  // Clipboard
  clipboard: ClipboardState
}

type Set = (fn: (state: StoreView) => void) => void
type Get = () => StoreView

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

export function createClipboardSlice(set: Set, get: Get, _api: unknown): ClipboardSlice {

  /**
   * Collect the selected CogoElement nodes from their block tree.
   * Returns an array of { element, blockName } tuples.
   */
  function getSelectedElements(): { element: CogoElement; blockName: string }[] {
    const { selectedIds, selectedBlockName, blocks } = get()
    if (!selectedBlockName || selectedIds.length === 0) return []

    const block = blocks[selectedBlockName]
    if (!block) return []

    const results: { element: CogoElement; blockName: string }[] = []
    for (const id of selectedIds) {
      const el = findElementInTree(block.ui.root, id)
      if (el) {
        results.push({ element: el, blockName: selectedBlockName })
      }
    }
    return results
  }

  return {
    clipboard: {
      elements: [],
      sourceBlockName: null,
      operation: null,
    },

    // ---- Copy ----
    copySelection: () => {
      const items = getSelectedElements()
      if (items.length === 0) return

      const cloned = items.map((item) => structuredClone(item.element))
      set((state) => {
        state.clipboard = {
          elements: cloned,
          sourceBlockName: items[0].blockName,
          operation: 'copy',
        }
      })
    },

    // ---- Cut ----
    cutSelection: () => {
      const items = getSelectedElements()
      if (items.length === 0) return

      const cloned = items.map((item) => structuredClone(item.element))

      // Immediately remove originals (single history snapshot for undo)
      set((state) => {
        state.clipboard = {
          elements: cloned,
          sourceBlockName: items[0].blockName,
          operation: 'cut',
        }
        // Remove originals from the tree
        const block = state.blocks[items[0].blockName]
        if (block) {
          for (const item of items) {
            const result = findParentInTree(block.ui.root, item.element.id)
            if (result && result.parent.children) {
              result.parent.children.splice(result.index, 1)
            }
          }
        }
        // Clear selection since elements are removed
        state.selectedIds = []
        state.selectedBlockName = null
      })
    },

    // ---- Paste ----
    pasteClipboard: () => {
      const { clipboard, selectedIds, selectedBlockName, blocks } = get()
      if (clipboard.elements.length === 0) return

      // Determine target block: selected block, or clipboard source, or first block on active page
      let targetBlockName = selectedBlockName ?? clipboard.sourceBlockName
      if (!targetBlockName) {
        // Fallback: find first block on active page
        const { app, activePageId } = get()
        if (app && activePageId) {
          const page = app.pages.find((p) => p.id === activePageId)
          if (page && page.blocks.length > 0) {
            targetBlockName = page.blocks[0]
          }
        }
      }
      if (!targetBlockName) return

      const block = blocks[targetBlockName]
      if (!block) return

      // Generate fresh clones with new IDs
      const newElements = clipboard.elements.map(deepCloneWithNewIds)

      set((state) => {
        const targetBlock = state.blocks[targetBlockName]
        if (!targetBlock) return

        // Determine insertion point:
        // If we have a selected element, insert after the last selected sibling.
        // Otherwise, append to the root's children.
        if (selectedIds.length > 0) {
          // Find the parent of the last selected element and insert after it
          const lastSelectedId = selectedIds[selectedIds.length - 1]
          const parentInfo = findParentInTree(targetBlock.ui.root, lastSelectedId)

          if (parentInfo && parentInfo.parent.children) {
            // Insert all new elements after the last selected one
            const insertAt = parentInfo.index + 1
            parentInfo.parent.children.splice(insertAt, 0, ...newElements)
          } else {
            // Selected element is the root itself — append to root's children
            if (!targetBlock.ui.root.children) targetBlock.ui.root.children = []
            targetBlock.ui.root.children.push(...newElements)
          }
        } else {
          // Nothing selected — append to root's children
          if (!targetBlock.ui.root.children) targetBlock.ui.root.children = []
          targetBlock.ui.root.children.push(...newElements)
        }

        // If this was a cut operation, clear clipboard (originals already removed at cut time)
        if (clipboard.operation === 'cut') {
          state.clipboard = {
            elements: [],
            sourceBlockName: null,
            operation: null,
          }
        }

        // Select the newly pasted elements
        state.selectedIds = newElements.map((el) => el.id)
        state.selectedBlockName = targetBlockName
      })
    },

    // ---- Duplicate ----
    duplicateSelection: () => {
      const { selectedIds, selectedBlockName, blocks } = get()
      if (!selectedBlockName || selectedIds.length === 0) return

      const block = blocks[selectedBlockName]
      if (!block) return

      // Collect elements to duplicate, filtering out descendants of other selected elements
      // to prevent double-duplication (ancestor clone already includes descendants)
      const allSelected: CogoElement[] = []
      for (const id of selectedIds) {
        const el = findElementInTree(block.ui.root, id)
        if (el) allSelected.push(el)
      }
      if (allSelected.length === 0) return

      const selectedIdSet = new Set(selectedIds)
      const elementsToDuplicate = allSelected.filter((el) => {
        // Keep only if no ancestor is also selected
        const parentInfo = findParentInTree(block.ui.root, el.id)
        if (!parentInfo) return true
        // Walk up the tree to check if any ancestor is also selected
        let current = parentInfo.parent
        while (current) {
          if (selectedIdSet.has(current.id)) return false
          const p = findParentInTree(block.ui.root, current.id)
          current = p ? p.parent : (undefined as unknown as CogoElement)
        }
        return true
      })

      set((state) => {
        const targetBlock = state.blocks[selectedBlockName]
        if (!targetBlock) return

        const newIds: string[] = []

        for (const origEl of elementsToDuplicate) {
          const clone = deepCloneWithNewIds(structuredClone(origEl))
          newIds.push(clone.id)

          // Insert clone right after the original
          const parentInfo = findParentInTree(targetBlock.ui.root, origEl.id)
          if (parentInfo && parentInfo.parent.children) {
            parentInfo.parent.children.splice(parentInfo.index + 1, 0, clone)
          } else {
            // The element is the root — append to root's children instead
            if (!targetBlock.ui.root.children) targetBlock.ui.root.children = []
            targetBlock.ui.root.children.push(clone)
          }
        }

        // Select the duplicated elements
        state.selectedIds = newIds
        state.selectedBlockName = selectedBlockName
      })
    },
  }
}
